import { useCascadingVariations } from "../../../hooks/useCascadingVariations";
import { MtcCascadingAttributes } from "../../ProductSingle/MtcCascadingAttributes";
import { MtcProductGallery } from "../../ProductSingle/MtcProductGallery";

const React = window.EcommerceCore.vendors.React;
const ReactDOM = window.EcommerceCore.vendors.ReactDOM;
const { useEffect, useRef, useState } = React;

const { useAppSelector, useAppDispatch } = window.EcommerceCore.common.hooks;
const { closeProductModal } = window.EcommerceCore.product.actions.modal;
const { useGetProductByIdQuery } = window.EcommerceCore.product.api;
const { useProductAddToCart } = window.EcommerceCore.cart.hooks;

const ProductPriceDisplay =
  window.EcommerceCore.product.components.ProductPriceDisplay;
const QuantityControl = window.EcommerceCore.cart.components.QuantityControl;
const Icon = window.EcommerceCore.common.components.Icon;
const { decodeHtmlEntities } = window.EcommerceCore.common.helpers;
const Skeleton =
  window.EcommerceCore.vendors.ReactLoadingSkeleton.default ||
  window.EcommerceCore.vendors.ReactLoadingSkeleton;

const MtcProductModal: React.FC = () => {
  const dispatch = useAppDispatch();

  const { isOpen, productId } = useAppSelector(
    (state: any) => state.productModal,
  );
  // Aktif para birimini Redux üzerinden dinle
  const currentCurrency = useAppSelector(
    (state: any) => state.currency.currentCurrency,
  );

  const panelRef = useRef<HTMLDivElement>(null);
  const portalRoot = document.body;

  const handleClose = () => {
    dispatch(closeProductModal());
  };

  const {
    data: product,
    isFetching: isProductFetching,
    isError: isProductError,
    refetch: refetchProduct, // Yeniden yükleme fonksiyonunu dışarı alıyoruz
  } = useGetProductByIdQuery(productId as number, {
    skip: !productId,
  });

  // Para birimi değiştiğinde ana ürünü de yeniden yükle (Modal açıksa)
  useEffect(() => {
    if (productId && isOpen) {
      refetchProduct();
    }
  }, [currentCurrency, productId, isOpen, refetchProduct]);

  const {
    selectedAttributes,
    selectedVariation,
    availableTermsPerAttribute,
    handleAttributeSelect,
    handleClear,
    isLoading: isSelectionLoading,
  } = useCascadingVariations(product);

  const { handleAddToCart, isAddingToCart } = useProductAddToCart();
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleClose();
    };

    if (isOpen) {
      document.body.classList.add("product-modal-open");
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.body.classList.remove("product-modal-open");
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen || !portalRoot) return null;

  const isComponentLoading = isProductFetching || isSelectionLoading;
  const displayData = selectedVariation || product;
  const isOutOfStock = displayData?.stockStatus === "outofstock";
  const isPurchasable = displayData?.purchasable;
  const isSelectionComplete = !product?.isVariable || !!selectedVariation;
  const isAddToCartDisabled =
    isAddingToCart || !isPurchasable || isOutOfStock || !isSelectionComplete;

  const onAddToCartClick = (e: any) => {
    const idToAdd = selectedVariation ? selectedVariation.id : product.id;
    const variationData = Object.entries(selectedAttributes).map(
      ([attribute, value]) => ({ attribute, value }),
    );
    handleAddToCart(e, {
      productId: idToAdd,
      quantity,
      variation: variationData,
    });

    handleClose();
  };

  return ReactDOM.createPortal(
    <div
      className={`product-modal-wrapper ${isOpen ? "is-open" : ""}`}
      aria-hidden={!isOpen}
    >
      <div className="product-modal__overlay" onClick={handleClose} />
      <div
        ref={panelRef}
        className="product-modal__panel mtc-product-modal mtc-product-single"
        role="dialog"
        aria-modal="true"
      >
        <div className="product-modal__header">
          <h2 className="product-modal__title">
            {product ? decodeHtmlEntities(product.name) : "Hızlı Bakış"}
          </h2>
          <button
            onClick={handleClose}
            className="product-modal__close-btn"
            aria-label="Kapat"
          >
            <Icon iconName="x-mark" className="icon--stroke-thickest" />
          </button>
        </div>

        <div className="product-modal__body">
          {isComponentLoading ? (
            <div style={{ padding: "20px" }}>
              <Skeleton height={350} />
            </div>
          ) : isProductError || !product ? (
            <p style={{ padding: "20px" }}>Ürün bilgisi yüklenemedi.</p>
          ) : (
            <div className="mtc-product-grid">
              {/* SOL: Görsel Galerisi */}
              <MtcProductGallery
                product={product}
                selectedVariation={selectedVariation}
              />

              {/* SAĞ: Bilgiler ve Custom Dropdownlar */}
              <div className="mtc-product-info">
                <ProductPriceDisplay
                  product={product}
                  selectedVariation={selectedVariation}
                  className="mtc-product-price"
                />

                <div
                  className="mtc-product-description"
                  dangerouslySetInnerHTML={{
                    __html:
                      displayData.shortDescription || product.shortDescription,
                  }}
                />

                <MtcCascadingAttributes
                  product={product}
                  selectedAttributes={selectedAttributes}
                  availableTermsPerAttribute={availableTermsPerAttribute}
                  onAttributeSelect={handleAttributeSelect}
                  onClear={handleClear}
                />

                <div className="mtc-product-actions">
                  <QuantityControl
                    value={quantity}
                    onChange={setQuantity}
                    min={1}
                    max={displayData.maxStock ?? 99}
                    disabled={isAddToCartDisabled}
                    ariaLabel="Miktar"
                  />
                  <button
                    className="button button--primary mtc-add-to-cart-btn"
                    onClick={onAddToCartClick}
                    disabled={isAddToCartDisabled}
                  >
                    {isAddingToCart ? "Ekleniyor..." : "Sepete Ekle"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    portalRoot,
  );
};

export default MtcProductModal;
